import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronDown, ArrowRight, Info } from 'lucide-react';
import InfoSpot from '@/components/InfoSpot';
import PortalShell from '@/components/PortalShell';
import AutoCursor from '@/components/AutoCursor';
import TypedText from '@/components/TypedText';

function WizardStepper({ active }) {
  const steps = ['Basic Details', 'Assign Roles', 'Review'];
  return (
    <div className="flex items-center justify-center py-4 px-6 bg-[#FAFAFA] rounded-xl mb-5">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className="flex flex-col items-center gap-1.5">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold border-2 ${
              i < active ? 'bg-teal-600 border-teal-600 text-white' :
              i === active ? 'bg-white border-teal-600 text-teal-600' :
              'bg-white border-[#E5E7EB] text-[#737373]'
            }`}>
              {i < active ? '✓' : <span className="w-2 h-2 rounded-full bg-current" />}
            </div>
            <span className={`text-[12px] font-body ${i <= active ? 'font-medium text-[#0A0A0A]' : 'text-[#737373]'}`}>{s}</span>
          </div>
          {i < steps.length - 1 && <div className={`w-24 h-[2px] mx-2 mt-[-18px] ${i < active ? 'bg-teal-600' : 'bg-[#E5E7EB]'}`} />}
        </div>
      ))}
    </div>
  );
}

function TypingField({ label, value, required, delay, prefix = null, flag = null, testid, instant = false }) {
  return (
    <div>
      <label className="text-[12px] font-body font-medium text-[#0A0A0A] mb-1.5 block">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
      <motion.div
        data-testid={testid}
        initial={{ borderColor: '#E5E7EB' }}
        animate={instant ? {} : { borderColor: ['#E5E7EB', '#0D9488', '#E5E7EB'] }}
        transition={{ duration: 0.8, delay, times: [0, 0.3, 1] }}
        className="w-full px-3 py-2 rounded-lg border bg-white text-sm font-body text-[#0A0A0A] flex items-center gap-2 min-h-[38px]"
      >
        {flag && <span className="text-lg">{flag}</span>}
        {prefix && <span className="text-[#737373] border-r border-[#E5E7EB] pr-2">{prefix}</span>}
        {instant ? <span>{value}</span> : <TypedText text={value} delay={delay} speed={20} />}
      </motion.div>
    </div>
  );
}

function SelectField({ label, value, openAt, selectAt, required, persistent = false, instant = false }) {
  const [showValue, setShowValue] = useState(instant);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (instant) return;
    const o = setTimeout(() => setOpen(true), openAt * 1000);
    const c = persistent ? null : setTimeout(() => setOpen(false), (openAt + 0.9) * 1000);
    const v = setTimeout(() => setShowValue(true), selectAt * 1000);
    return () => { clearTimeout(o); if (c) clearTimeout(c); clearTimeout(v); };
  }, [openAt, selectAt, persistent, instant]);
  return (
    <div className="relative">
      <label className="text-[12px] font-body font-medium text-[#0A0A0A] mb-1.5 block">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
      <motion.div
        animate={{
          borderColor: open ? '#0D9488' : '#E5E7EB',
          boxShadow: open ? '0 0 0 3px rgba(13,148,136,0.2)' : '0 0 0 0 transparent',
        }}
        className="w-full px-3 py-2 rounded-lg border bg-white text-sm font-body text-[#0A0A0A] flex items-center justify-between min-h-[38px]"
      >
        {showValue
          ? <motion.span initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="font-medium">{value}</motion.span>
          : <span className="text-[#A3A3A3]">Select…</span>}
        <ChevronDown size={14} className="text-[#737373]" />
      </motion.div>
    </div>
  );
}

function FacilityTagsField({ label, values, openAt, addDelays = [], required, persistent = false, instant = false }) {
  const [visibleCount, setVisibleCount] = useState(instant ? values.length : 0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (instant) return;
    const o = setTimeout(() => setOpen(true), openAt * 1000);
    const c = persistent ? null : setTimeout(() => setOpen(false), (openAt + addDelays[addDelays.length - 1] + 0.5) * 1000);
    const timers = addDelays.map((d, i) =>
      setTimeout(() => setVisibleCount(i + 1), (openAt + d) * 1000)
    );
    return () => { clearTimeout(o); if (c) clearTimeout(c); timers.forEach(clearTimeout); };
  }, [openAt, addDelays, persistent, instant]);
  return (
    <div>
      <label className="text-[12px] font-body font-medium text-[#0A0A0A] mb-1.5 block">
        {label}{required && <span className="text-red-500">*</span>}
      </label>
      <motion.div
        animate={{
          borderColor: open ? '#0D9488' : '#E5E7EB',
          boxShadow: open ? '0 0 0 3px rgba(13,148,136,0.2)' : '0 0 0 0 transparent',
        }}
        className="w-full px-3 py-2 rounded-lg border bg-white text-sm font-body flex items-center gap-2 flex-wrap min-h-[38px]"
      >
        <AnimatePresence>
          {values.slice(0, visibleCount).map(v => (
            <motion.span
              key={v}
              initial={{ opacity: 0, scale: 0.6, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ type: 'spring', stiffness: 380, damping: 22 }}
              className="px-2.5 py-1 rounded-md bg-teal-600/10 text-teal-700 text-xs font-medium flex items-center gap-1"
            >
              {v} <span className="text-teal-600 cursor-pointer">×</span>
            </motion.span>
          ))}
        </AnimatePresence>
        {visibleCount < values.length && <span className="text-[#A3A3A3] text-xs">Select facility…</span>}
      </motion.div>
    </div>
  );
}

/**
 * Explainer panel describing both org-based and standalone facility scenarios.
 */
function FacilityExplainer({ delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.5 }}
      className="mt-5 rounded-2xl border-2 border-teal-600/30 bg-gradient-to-br from-teal-50 to-white p-5 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Info size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading font-bold text-[15px] text-[#0A0A0A] mb-1">How facility selection works</h4>
          <div className="space-y-2.5 text-[12.5px] font-body text-[#374151] leading-relaxed">
            <div className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-teal-600 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</div>
              <p>
                <span className="font-semibold text-teal-700">Facility under an organisation </span>
                — pick the <span className="font-medium">Organisation</span> first (e.g. <span className="px-1.5 py-0.5 rounded bg-white border border-teal-200 text-teal-700 text-[11px] font-medium">Burjeel</span>).
                The <span className="font-medium">Linked facility</span> dropdown then shows only facilities that belong to it.
                You can link one user to <span className="font-semibold">multiple facilities</span> inside the same organisation.
              </p>
            </div>
            <div className="flex gap-2">
              <div className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</div>
              <p>
                <span className="font-semibold text-amber-700">Standalone facility </span>
                — if the facility is not part of an organisation, the Organisation field is <span className="font-medium">skipped</span>.
                You select the standalone facility directly in the <span className="font-medium">Linked facility</span> field.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Context banner that appears in `quickFill` mode to tell the viewer that
 * this form is pre-filled to set context for the next step.
 */
function QuickFillBanner() {
  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }}
      className="mt-3 mb-4 rounded-xl bg-teal-50 border border-teal-600/30 px-4 py-2.5 flex items-center gap-3"
    >
      <div className="w-6 h-6 rounded-full bg-teal-600 flex items-center justify-center flex-shrink-0">
        <span className="text-white text-[11px] font-bold">i</span>
      </div>
      <p className="font-body text-[12px] text-teal-900">
        Basic details are already filled for <span className="font-semibold">Ahmed Shareef</span>. Continuing to role assignment…
      </p>
    </motion.div>
  );
}

export default function UserFormBasicScene({ onNext, facilityFocus = false, quickFill = false }) {
  // Regular (full-wizard) timing — username & email both type from the same moment
  const regularTimings = {
    firstName: 4.2, lastName: 4.6,
    username: 5.0, email: 5.0,   // mirror each other
    contact: 6.6,
    userTypeOpen: 7.2, userTypeSelect: 7.8,
    nationality: 8.0, eid: 8.6,
    orgOpen: 9.4, orgSelect: 10.0,
    facilityOpen: 10.3, facilityDelays: [0.4, 1.2],
    nextButton: 12.0,
  };

  // Cursor path adapts to mode
  const cursorPath = quickFill
    ? [
        { x: 600, y: 200, delay: 0.6 },
        { x: 560, y: 600, delay: 1.0, click: true },   // point at Next CTA
      ]
    : facilityFocus
    ? [
        { x: 400, y: 80,  delay: 0.6 },
        { x: 400, y: 180, delay: 1.0 },                 // personal fields
        { x: 800, y: 260, delay: 1.4 },
        { x: 400, y: 440, delay: 5.8 },                 // organisation
        { x: 800, y: 440, delay: 3.0 },                 // linked facility
        { x: 800, y: 440, delay: 2.5 },                 // linger
      ]
    : [
        { x: 400, y: 80,  delay: 0.6 },
        { x: 400, y: 180, delay: 1.2 },
        { x: 800, y: 260, delay: 1.6 },
        { x: 400, y: 440, delay: 1.6 },
        { x: 800, y: 440, delay: 2.0 },
        { x: 560, y: 600, delay: 1.6, click: true },
      ];

  return (
    <PortalShell
      activeItem="User"
      overlay={cursorPath.length ? <AutoCursor targets={cursorPath} startDelay={facilityFocus ? 0.4 : 0.8} /> : null}
    >
      <div data-testid="scene-user-form-basic" className="px-8 pt-5 pb-8">
        <div className="max-w-3xl mx-auto">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[12px] font-body text-[#737373] mb-3">
            Manage <ChevronRight size={12} className="inline" /> Users <ChevronRight size={12} className="inline" /> <span className="text-[#0A0A0A] font-medium">Add new user</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
            <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0A0A0A] flex items-center gap-2">
              Add New User
              {!facilityFocus && !quickFill && (
                <InfoSpot
                  text="Step 1 of 3 — Basic Details. Fill personal info, organisation and linked facilities. Username auto-matches business email. Users can belong to multiple facilities in the same organisation."
                  position="right"
                  autoOpenAt={0.8}
                  autoCloseAfter={3.0}
                  glowAt={0.5}
                />
              )}
            </h2>
            <p className="mt-1 font-body text-sm text-[#737373]">Create a new user and set access permissions</p>
          </motion.div>

          {quickFill && <QuickFillBanner />}

          <WizardStepper active={0} />

          {/* Full "Add New User" form template — used in all modes */}
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-[#E5E7EB] shadow-sm p-5"
          >
            <h3 className="font-heading font-bold text-base text-[#0A0A0A] mb-1">Basic details</h3>
            <p className="font-body text-xs text-[#737373] mb-4">Fill in the required basic information</p>

            <div className="grid grid-cols-2 gap-x-5 gap-y-4">
              <TypingField label="First name" value="Ahmed" required delay={regularTimings.firstName} instant={quickFill} />
              <TypingField label="Last name" value="Shareef" required delay={regularTimings.lastName} instant={quickFill} />
              {/* Original order: Username first, Business email second — both mirror from the same typing delay */}
              <TypingField label="Username" value="asamoe@yourhealth.com" delay={regularTimings.username} testid="field-username" instant={quickFill} />
              <TypingField label="Business email address" value="asamoe@yourhealth.com" required delay={regularTimings.email} testid="field-business-email" instant={quickFill} />
              <TypingField label="Contact number" value="512 100999" required prefix="+971" delay={regularTimings.contact} instant={quickFill} />
              <SelectField label="User type" value="Doctor" openAt={regularTimings.userTypeOpen} selectAt={regularTimings.userTypeSelect} instant={quickFill} />
              <TypingField label="Nationality" value="Algeria" delay={regularTimings.nationality} flag="🇩🇿" instant={quickFill} />
              <TypingField label="Emirates ID number" value="784-1939-1111111-1" required delay={regularTimings.eid} instant={quickFill} />
              <SelectField
                label="Organisation"
                value="Burjeel"
                required
                openAt={regularTimings.orgOpen}
                selectAt={regularTimings.orgSelect}
                instant={quickFill}
                persistent={facilityFocus}
              />
              <FacilityTagsField
                label="Linked facility"
                values={['Burjeel Al Nahyan', 'Burjeel - Mussafah']}
                required
                openAt={regularTimings.facilityOpen}
                addDelays={regularTimings.facilityDelays}
                instant={quickFill}
                persistent={facilityFocus}
              />
            </div>
          </motion.div>

          {/* After-form content depends on mode */}
          {facilityFocus && <FacilityExplainer delay={regularTimings.nextButton} />}

          {!facilityFocus && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: quickFill ? 1.2 : regularTimings.nextButton }}
              className="flex justify-center mt-5"
            >
              <motion.button
                data-testid="wizard-next-basic"
                onClick={onNext}
                animate={{
                  scale: [1, 1, 1.1, 0.97, 1.06, 1],
                  boxShadow: [
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 10px rgba(13,148,136,0.3)',
                    '0 0 0 0 rgba(13,148,136,0)',
                    '0 0 0 8px rgba(13,148,136,0.25)',
                    '0 0 0 0 rgba(13,148,136,0)',
                  ],
                }}
                transition={{ duration: 2.4, delay: (quickFill ? 1.4 : regularTimings.nextButton + 0.2), times: [0, 0.2, 0.35, 0.5, 0.75, 1] }}
                className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-teal-600 text-white font-body font-bold text-sm hover:bg-teal-700 transition-colors"
              >
                Next <ArrowRight size={16} />
              </motion.button>
            </motion.div>
          )}
        </div>
      </div>
    </PortalShell>
  );
}
